import React, { createContext, useContext, useCallback } from 'react';

interface AuthContextValue {
  user: any;
  logout: () => void;
  hasPermission: (module: string, action: string) => boolean;
  hasModuleAccess: (module: string) => boolean;
  isSuperAdmin: boolean;
  permissions: Record<string, string[]>;
  hasAnyPermissions: boolean; // true if user has permissions data loaded
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  logout: () => {},
  hasPermission: () => false,
  hasModuleAccess: () => false,
  isSuperAdmin: false,
  permissions: {},
  hasAnyPermissions: false,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ user: any; logout: () => void; children: React.ReactNode }> = ({ user, logout, children }) => {
  const permissions: Record<string, string[]> = user?.permissions || {};
  const isSuperAdmin = user?.isSuperAdmin || false;

  const hasPermission = useCallback((module: string, action: string): boolean => {
    if (isSuperAdmin) return true;
    const modulePerms = permissions[module];
    if (!modulePerms) return false;
    return modulePerms.includes(action);
  }, [permissions, isSuperAdmin]);

  const hasModuleAccess = useCallback((module: string): boolean => {
    if (isSuperAdmin) return true;
    return !!permissions[module] && permissions[module].length > 0;
  }, [permissions, isSuperAdmin]);

  const hasAnyPermissions = Object.keys(permissions).length > 0 || isSuperAdmin;

  return (
    <AuthContext.Provider value={{ user, logout, hasPermission, hasModuleAccess, isSuperAdmin, permissions, hasAnyPermissions }}>
      {children}
    </AuthContext.Provider>
  );
};
